import { useState } from 'react';

import { getServiceId } from '@/api/tokens.helper.ts';
import { FRONTEND_FEATURES } from '@/permissions/frontend-features';
import { useFrontendFeature } from '@/permissions/useFrontendFeature';
import type { Invoice } from '@/store/expenses-store/invoices/invoices.dto';
import { useTransactionsStore } from '@/store/expenses-store/transactions/transactions.store.ts';
import { getSingleServiceProvider } from '@/store/service-providers/service-providers.api.ts';
import { toast } from '@/utils/toast.ts';
import { buildPaymentBody } from '@pages/Expenses/components/modals/buildPaymentBody';
import {
  openPaymentCheckout,
  paymentReturnUrl,
} from '@pages/Expenses/components/modals/openPaymentCheckout';

export const useInvoicePayment = () => {
  const createPayment = useTransactionsStore((state) => state.createPayment);
  const vesselId = getServiceId();
  const [payingId, setPayingId] = useState<string | null>(null);
  const canPay = useFrontendFeature(FRONTEND_FEATURES.PaymentsUI);

  const payInvoice = async (invoice: Invoice) => {
    // One provider, fetched by the id the invoice already carries, at the moment it is
    // needed. This used to hold a hundred of them: the hook ran a service-providers
    // list query at mount and picked one out of it with `.find()`, which meant every
    // surface that so much as mounts this hook — the invoice modal, and so anything
    // that mounts *that*, including a task card that never shows an invoice — paid for
    // a hundred-row fetch to read one email off one row.
    let provider = null;
    try {
      provider = invoice.service_provider_id
        ? await getSingleServiceProvider(invoice.service_provider_id, vesselId)
        : null;
    } catch (error) {
      console.error('Failed to load the invoice vendor:', error);
    }

    const recipientEmail = provider?.contact_info?.email;

    if (!recipientEmail) {
      toast.error('Couldn’t send payment. Please add the vendor’s email.');
      return;
    }

    const returnUrl = paymentReturnUrl();
    const body = buildPaymentBody({
      amount: invoice.amount?.number ?? '0',
      recipientEmail,
      recipientName: provider?.name ?? invoice.company_name ?? undefined,
      description: invoice.invoice_number ? `Invoice #${invoice.invoice_number}` : undefined,
      successUrl: returnUrl,
      invoice_id: invoice.id as string,
      cancelUrl: returnUrl,
    });

    setPayingId(invoice.id);
    try {
      const url = await createPayment(body);
      if (url) await openPaymentCheckout(url);
    } finally {
      setPayingId(null);
    }
  };

  return { canPay, payInvoice, payingId };
};
